import { useState } from 'react';

type TabType = 'refund' | 'terms';

const REFUND_ITEMS = [
  {
    icon: 'ri-calendar-check-line',
    title: '課程退款 · 7 天無理由',
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    content:
      '購買課程後 7 天內（含購買當日），如課程累計學習進度未超過 20%，可申請全額退款。退款將在審核通過後 5–7 個工作日內原路返還。',
  },
  {
    icon: 'ri-video-chat-line',
    title: '1V1 導師諮詢 · 48 小時取消',
    color: 'text-violet-500',
    bg: 'bg-violet-50',
    content:
      '預約確認後，若在諮詢開始前 48 小時以上取消，可獲全額退款；48 小時內取消，退還 50% 費用；諮詢開始後不予退款。導師因故取消，平台將全額退還並贈送下次諮詢 10% 折扣券。',
  },
  {
    icon: 'ri-vip-crown-line',
    title: '會員訂閱 · 月度可退',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    content:
      '月度會員在訂閱後 3 天內可申請全額退款；年度會員在訂閱後 7 天內可申請全額退款。超出時限後的退款請求，將按照剩餘月數折算處理。',
  },
  {
    icon: 'ri-close-circle-line',
    title: '不適用退款的情形',
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    content:
      '以下情形不予退款：課程學習進度超過 20%、已參加結課考試、因違反平台規定被封號、透過促銷活動獲得的贈課或免費體驗課程。',
  },
];

const TERMS_SECTIONS = [
  {
    title: '1. 服務說明',
    content:
      'NexusGo 提供線上 AI 職業教育課程、1V1 導師諮詢及職涯規劃相關服務。平台有權根據業務發展隨時調整服務內容，重大變更將提前 14 天以電子郵件形式通知使用者。',
  },
  {
    title: '2. 帳號與安全',
    content:
      '使用者須保管好帳號及密碼，不得將帳號轉讓或借用他人。如發現帳號異常，請立即聯絡客服。因使用者個人原因導致的帳號安全問題，平台不承擔賠償責任。',
  },
  {
    title: '3. 智慧財產權',
    content:
      '平台上所有課程內容、影片、文字、音訊及相關材料均受著作權保護，歸 NexusGo 或內容創作者所有。使用者僅獲得個人學習使用權，嚴禁錄製、轉載、散布或任何形式的商業使用。',
  },
  {
    title: '4. 使用者行為規範',
    content:
      '使用者使用服務時須遵守所在地法律法規，不得傳播違法內容、騷擾導師或其他使用者、惡意刷單或利用系統漏洞獲取不當利益。違規行為將導致帳號封禁，情節嚴重者將追究法律責任。',
  },
  {
    title: '5. 隱私保護',
    content:
      '我們嚴格遵守隱私保護原則，使用者個人資訊僅用於提供平台服務及改善使用者體驗，不會出售給任何第三方。詳細內容請參閱《隱私政策》。',
  },
  {
    title: '6. 免責聲明',
    content:
      '平台上的導師均為獨立從業者，其提供的職業建議僅供參考。NexusGo 不保證使用者在接受諮詢或學習課程後必然獲得特定職位或薪資結果。平台在法律允許的最大範圍內，不對間接損失或結果性損失承擔責任。',
  },
  {
    title: '7. 條款變更',
    content:
      '本條款最後更新日期為 2026 年 1 月 1 日。NexusGo 保留隨時修改本條款的權利。修改後繼續使用平台服務，即視為同意修改後的條款。',
  },
];

const LegalSection = () => {
  const [activeTab, setActiveTab] = useState<TabType>('refund');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="border-t border-slate-100 py-10 sm:py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6 sm:mb-8">
          <div className="w-1 h-5 bg-slate-300 rounded-full" />
          <h3 className="text-base font-bold text-[#0A1628]">法律與政策</h3>
          <span className="text-xs text-slate-400 font-medium ml-1">最後更新：2026 年 1 月</span>
        </div>

        {/* Tab Switcher */}
        <div className="inline-flex bg-slate-100 rounded-full p-1 mb-7 gap-1">
          {[
            { id: 'refund' as TabType, label: '退款政策', icon: 'ri-refund-2-line' },
            { id: 'terms' as TabType, label: '服務條款', icon: 'ri-file-shield-2-line' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-white text-[#0A1628]'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Refund Policy */}
        {activeTab === 'refund' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {REFUND_ITEMS.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl border border-slate-100 p-5 hover:border-slate-200 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 ${item.bg}`}>
                    <i className={`${item.icon} text-base ${item.color}`} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0A1628] mb-1.5">{item.title}</p>
                    <p className="text-xs text-slate-500 leading-relaxed">{item.content}</p>
                  </div>
                </div>
              </div>
            ))}

            <div className="sm:col-span-2 bg-slate-50 rounded-2xl border border-slate-100 px-5 py-4 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-slate-400 text-sm" />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                退款申請請傳送郵件至{' '}
                <strong className="text-[#0A1628]">admin@nexusgo.ai</strong>，
                標題注明「退款申請 · 訂單號」，我們將在 2 個工作日內回覆。
                如有爭議，最終解釋權歸 NexusGo 所有。
              </p>
            </div>
          </div>
        )}

        {/* Terms of Service */}
        {activeTab === 'terms' && (
          <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
            {TERMS_SECTIONS.map((section, i) => (
              <div
                key={section.title}
                className={`border-b border-slate-100 last:border-0 ${
                  expandedIndex === i ? 'bg-slate-50/60' : ''
                }`}
              >
                <button
                  onClick={() => setExpandedIndex(expandedIndex === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 sm:px-6 py-4 text-left cursor-pointer hover:bg-slate-50 transition-colors"
                >
                  <span className={`text-sm font-semibold transition-colors ${
                    expandedIndex === i ? 'text-violet-700' : 'text-[#0A1628]'
                  }`}>
                    {section.title}
                  </span>
                  <div className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ml-4 transition-all ${
                    expandedIndex === i ? 'bg-violet-100 rotate-180' : 'bg-slate-100'
                  }`}>
                    <i className={`ri-arrow-down-s-line text-sm ${
                      expandedIndex === i ? 'text-violet-600' : 'text-slate-400'
                    }`} />
                  </div>
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-out"
                  style={{ maxHeight: expandedIndex === i ? '200px' : '0px' }}
                >
                  <p className="px-5 sm:px-6 pb-5 text-xs text-slate-500 leading-relaxed">
                    {section.content}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LegalSection;
